import { clsx } from 'clsx';
import * as React from 'react';
import { mergeProps, useFocusable, useObjectRef } from 'react-aria';
import {
    Tooltip as RACTooltip,
    type TooltipProps as RACTooltipProps,
    type TooltipRenderProps,
    TooltipTrigger,
    type TooltipTriggerComponentProps,
} from 'react-aria-components';

import { KeyboardShortcut } from '../KeyboardShortcut';

export type TooltipPosition = RACTooltipProps['placement'];

export type TooltipProps = {
    /**
     * The content of the tooltip.
     * @deprecated Use `label` instead.
     */
    tooltip?: React.ReactNode;
    /**
     * Disables the tooltip.
     */
    disabled?: boolean;
    /**
     * The delay before the tooltip is shown.
     * @default 300
     */
    delay?: number;
    /**
     * The offset between the trigger and the tooltip.
     * @default 4
     */
    offset?: number;
    /**
     * The trigger of the tooltip.
     */
    children: React.ReactElement;

    position?: TooltipPosition;
    disableHoverableContent?: boolean;
    isOpen?: TooltipTriggerComponentProps['isOpen'];
    onOpenChange?: TooltipTriggerComponentProps['onOpenChange'];
} & Partial<TooltipLayoutProps>;

export function getTooltipAnimationClassName(props: TooltipRenderProps): string {
    return clsx(
        'fill-mode-forwards',
        props.placement &&
            {
                bottom: 'origin-top',
                top: 'origin-bottom',
                left: 'origin-right',
                right: 'origin-left',
                center: 'origin-center',
            }[props.placement],
        props.isEntering &&
            clsx(
                'fade-in animate-in',
                props.placement &&
                    {
                        bottom: 'slide-in-from-top-1',
                        top: 'slide-in-from-bottom-1',
                        left: 'slide-in-from-right-1',
                        right: 'slide-in-from-left-1',
                        center: '',
                    }[props.placement]
            ),
        props.isExiting &&
            clsx(
                'fade-out zoom-out-95 animate-out',
                props.placement &&
                    {
                        bottom: 'slide-out-to-top-1',
                        top: 'slide-out-to-bottom-1',
                        left: 'slide-out-to-right-1',
                        right: 'slide-out-to-left-1',
                        center: '',
                    }[props.placement]
            )
    );
}

type TooltipOverlayProps = RACTooltipProps & {
    disableHoverableContent?: boolean;
    children: React.ReactNode;
};

const TooltipOverlay = React.forwardRef(
    (
        { className, disableHoverableContent = true, children, ...props }: TooltipOverlayProps,
        ref: React.ForwardedRef<HTMLDivElement>
    ) => {
        const frozenChildrenRef = React.useRef(children);
        return (
            <RACTooltip
                ref={ref}
                {...props}
                className={(props) =>
                    clsx(
                        'z-tooltip flex w-fit max-w-64 flex-col gap-1 rounded bg-tile-inverted px-2 py-1 text-inverted transition-opacity',
                        disableHoverableContent && 'pointer-events-none',
                        getTooltipAnimationClassName(props),
                        className
                    )
                }
            >
                {(values) => {
                    // Freeze the children while the tooltip is animating.
                    if (!values.isEntering && !values.isExiting) {
                        frozenChildrenRef.current = children;
                        return children;
                    }

                    return frozenChildrenRef.current;
                }}
            </RACTooltip>
        );
    }
);

function TooltipTarget(props: { children: React.ReactElement }) {
    const { children } = props;
    const triggerRef = useObjectRef<HTMLElement>((children as any).ref);
    const { focusableProps } = useFocusable({}, triggerRef);

    return React.cloneElement(
        children,
        mergeProps({ ...focusableProps, tabIndex: 0, ref: triggerRef }, children.props)
    );
}

export const TooltipContext = React.createContext<{ disabled?: boolean }>({});

export const Tooltip = function Tooltip(props: TooltipProps) {
    const ctx = React.useContext(TooltipContext);
    if (process.env.GITBOOK_PREDICTABLE_UI) {
        return React.cloneElement(props.children);
    }
    const isDisabled = props.disabled || ctx.disabled;
    if (!props.label && !props.tooltip) {
        return React.cloneElement(props.children);
    }
    return (
        <TooltipTrigger
            delay={props.delay ?? 300}
            closeDelay={50}
            isOpen={props.isOpen}
            onOpenChange={props.onOpenChange}
            isDisabled={isDisabled}
        >
            <TooltipTarget>{props.children}</TooltipTarget>
            <TooltipOverlay
                placement={props.position}
                disableHoverableContent={props.disableHoverableContent}
                offset={props.offset ?? 4}
            >
                {props.tooltip !== undefined ? (
                    props.tooltip
                ) : props.label ? (
                    <TooltipLayout {...props} label={props.label} />
                ) : null}
            </TooltipOverlay>
        </TooltipTrigger>
    );
};

type TooltipLayoutProps = {
    /**
     * The main label for the Tooltip.
     */
    label: string | null;

    /*
     * A keyboard shortcut to be shown in the Tooltip
     */
    shortcut?: string;

    /**
     * A descriptive block of text
     *
     * Recommended that it isn't too long but can be multiline.
     */
    description?: string;
};

function TooltipLayout(props: TooltipLayoutProps) {
    const { label, shortcut, description } = props;
    return (
        <>
            <div className="flex gap-2">
                <span className={clsx('flex-1 text-ui-base', shortcut && 'truncate')}>{label}</span>
                {shortcut ? (
                    <KeyboardShortcut
                        /**
                         * max items : 4
                         * item width: 20px
                         * item gap : 4px
                         * max width : max-items * item-width + max-items - 1 * item-gap = 92px
                         */
                        className="max-w-[5.75rem] overflow-hidden"
                        shortcut={shortcut}
                        inverted
                    />
                ) : null}
            </div>
            {description ? <p className="text-ui-small">{description}</p> : null}
        </>
    );
}
